/**
 * The typed game-state API.
 *
 * `GameState` is a plain immutable snapshot produced once per simulation tick.
 * It is the ONLY thing controllers are allowed to see; nothing in the
 * controller layer may reach into the engine. That constraint is what makes it
 * possible to swap a keyboard for a bot for a stimulated human arm without
 * touching game code.
 */

export interface GameConfig {
  /** Logical canvas size in pixels. Rendering scales to fit. */
  width: number;
  height: number;

  /** Fixed horizontal position of the bird, in pixels. */
  birdX: number;
  birdRadius: number;

  /** Downward acceleration, px/s^2. Zero is allowed; see `drag`. */
  gravity: number;
  /**
   * Linear drag coefficient, 1/s. `dv/dt = gravity - drag * v`.
   *
   * This is what makes low gravity playable. Without it the bird accelerates
   * until it hits `maxFallSpeed`, so halving gravity just delays the same
   * runaway dive and the pilot still has to intervene constantly. With drag the
   * bird settles at a steady sink rate of `gravity / drag`, and the interval
   * between required flaps is set by that sink rate rather than by an
   * accelerating fall.
   *
   * 0 restores the original undamped behaviour exactly.
   */
  drag: number;
  /** Instantaneous vertical velocity applied on flap, px/s (negative = up). */
  flapImpulse: number;
  /** Hard cap on downward speed, px/s. With drag the natural terminal velocity
   * (`gravity / drag`) usually binds first and this is only a safety net. */
  maxFallSpeed: number;

  /** Horizontal pipe scroll speed, px/s. */
  pipeSpeed: number;
  /** Vertical opening between the two pipe halves, px. */
  pipeGap: number;
  pipeWidth: number;
  /** Horizontal distance between consecutive pipe leading edges, px. */
  pipeSpacing: number;
  /** Distance from the top/bottom of the play field the gap may not enter. */
  gapMargin: number;
  /**
   * Largest vertical step between consecutive gap centres, px. 0 = unlimited.
   *
   * A forgiveness lever that is invisible on screen: each pipe still looks
   * randomly placed, but the course stops demanding a full-height climb between
   * two pipes, which is where most of the required flaps go.
   */
  maxGapCenterStep: number;
  /** Horizontal offset of the first pipe past the right edge, px. */
  firstPipeOffset: number;

  /** Fixed simulation rate in Hz. All physics runs at exactly this rate. */
  simHz: number;

  /**
   * Collision circle radius as a fraction of `birdRadius`. 1 = the drawn body.
   *
   * The other invisible forgiveness lever. The sprite already overhangs its
   * hitbox; shrinking the hitbox further makes near misses survivable without
   * changing a single pixel on screen. The bot-vision overlay draws the true
   * circle, so nothing is hidden from the experimenter.
   */
  hitboxScale: number;
  /**
   * How long the bird may overlap a pipe before it counts as a collision, ms.
   *
   * Temporal forgiveness rather than spatial: a flap that arrives slightly late
   * can still rescue a clip. That is precisely the failure mode a stimulated
   * human actuator produces, so this is the lever that buys tolerance for a
   * slow participant rather than for a badly aimed one. Applies to pipes only;
   * the ground is always fatal.
   */
  collisionGraceMs: number;

  /** RNG seed. Same seed + same flap ticks => bit-identical run. */
  seed: number;

  /** If true, touching the ceiling ends the run; otherwise velocity clamps. */
  ceilingKills: boolean;
}

/**
 * Default course.
 *
 * Tuned for one number above all others: **how often a participant has to be
 * stimulated**. Every flap in a mediated run is a real electrical stimulus
 * delivered to a person, so the required flap rate is an ethical budget, not
 * just a difficulty setting.
 *
 * These defaults are the `balanced` preset: a direct bot needs about 1.11 flaps
 * per second, against 2.66 for the original undamped course, with no loss of
 * reliability. See `Difficulty.ts` for the full set of presets, for what each
 * lever costs in visibility, for why a gentler course is also a less sensitive
 * experiment, and for why nothing much below 1 flap/s survives at the original
 * 240 px pipe spacing at any gravity.
 */
export const DEFAULT_CONFIG: GameConfig = {
  width: 480,
  height: 640,
  birdX: 120,
  birdRadius: 12,
  gravity: 260,
  drag: 1.3,
  flapImpulse: -250,
  maxFallSpeed: 600,
  pipeSpeed: 130,
  pipeGap: 170,
  pipeWidth: 60,
  pipeSpacing: 260,
  gapMargin: 70,
  maxGapCenterStep: 80,
  firstPipeOffset: 160,
  hitboxScale: 0.85,
  collisionGraceMs: 40,
  simHz: 120,
  seed: 12345,
  ceilingKills: false,
};

export interface PipeSnapshot {
  id: number;
  /** Leading (left) edge, px. */
  x: number;
  gapTop: number;
  gapBottom: number;
  scored: boolean;
}

/**
 * Snapshot handed to every controller, once per simulation tick.
 *
 * The fields above the divider are the required minimum from the experiment
 * spec; everything below exists so a controller can compute look-ahead terms
 * without knowing engine internals.
 */
export interface GameState {
  /** performance.now() at the moment this tick was simulated (wall clock, ms). */
  timestamp: number;
  tick: number;

  birdY: number;
  birdVelocityY: number;

  nextPipeX: number;
  nextPipeGapTop: number;
  nextPipeGapBottom: number;

  score: number;
  alive: boolean;

  // ---------------------------------------------------------------- extras --

  /** Simulation clock: tick * (1000 / simHz). Monotonic, jitter-free, ms. */
  simTimeMs: number;
  /** Whether a flap was actually executed on this tick. */
  flapped: boolean;
  /** Constant, but exposed so controllers stay engine-agnostic. */
  birdX: number;
  /** Drawn body radius, px. */
  birdRadius: number;
  /** Radius actually used for collision, px. Never larger than `birdRadius`. */
  collisionRadius: number;
  gravity: number;
  drag: number;
  flapImpulse: number;
  pipeSpeed: number;
  pipeWidth: number;
  height: number;
  /** All pipes currently on screen, nearest first. */
  pipes: PipeSnapshot[];
  /** Ticks the bird has been alive this run. */
  aliveTicks: number;
}

/** Quantities every controller wants but nobody should recompute by hand. */
export interface DerivedState {
  /** Horizontal gap from bird centre to the pipe's leading edge, px. */
  distanceToNextPipe: number;
  /** Vertical centre of the next gap, px. */
  gapCenterY: number;
  /** birdY - gapCenterY. Positive means the bird is BELOW the gap centre. */
  verticalDistanceToGapCenter: number;
  /** Seconds until the pipe's leading edge reaches the bird. */
  timeToPipeSec: number;
  timeToPipeMs: number;
  /** Seconds until the pipe's trailing edge clears the bird. */
  timeToPipeExitSec: number;
  birdVelocityY: number;
  flapped: boolean;
}

export function derive(state: GameState): DerivedState {
  const distanceToNextPipe = state.nextPipeX - state.birdX;
  const gapCenterY = (state.nextPipeGapTop + state.nextPipeGapBottom) / 2;
  const timeToPipeSec = distanceToNextPipe / state.pipeSpeed;
  const timeToPipeExitSec = (distanceToNextPipe + state.pipeWidth) / state.pipeSpeed;
  return {
    distanceToNextPipe,
    gapCenterY,
    verticalDistanceToGapCenter: state.birdY - gapCenterY,
    timeToPipeSec,
    timeToPipeMs: timeToPipeSec * 1000,
    timeToPipeExitSec,
    birdVelocityY: state.birdVelocityY,
    flapped: state.flapped,
  };
}

/** Cheap structural copy, used by the logger so snapshots cannot be mutated. */
export function cloneState(state: GameState): GameState {
  return { ...state, pipes: state.pipes.map((p) => ({ ...p })) };
}
